// src/components/analytics/PrescriptiveActionCard.js
// Enhanced Production-Ready AI Prescription Card Component
// Features: Modern UI/UX, animations, dark theme support, visual analytics, debugging
// ENHANCEMENT: Safe ID parsing, Custom Chart Tooltips, Full ML context, WebSocket Live Updates, and Integrated Priority Queue
// --- UPDATE (FlowSense AI Explainer v1)
//   • "Why repair / replace?" FlowSense AI explainer: rule findings from this prescription's own
//     LCCA numbers (cost gap, confidence, RUL, failure risk) + a plain-language AI explanation.
//   • Live updates: the WebSocket listener now matches the payload FlowSense actually sends
//     (asset_id or assetId, no `type` field when subscribed by type); confidence 0–1 is shown as %.
//   • No invented numbers: confidence with no value shows "—" (was a hard-coded 85 %); the
//     "new asset" bar shows its real share (was a fixed 40 %); ML metrics show whenever the ML
//     engine sent them (were hidden unless an `additional_metrics` flag was set); failure risk
//     0–1 shown as %; "generated at" uses the prescription's own timestamp when present.
//   • Telemetry modal: shows how many survival points / history rows came back and a Retry.
// --- END UPDATE

import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  ResponsiveContainer, Tooltip as RechartsTooltip, AreaChart, Area
} from 'recharts';
import {
  Box, Text, Heading, Badge, VStack, HStack, Icon, Divider, Progress, Circle,
  useColorModeValue, Flex, Tooltip, IconButton, Collapse, useDisclosure,
  SimpleGrid, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, CircularProgress, CircularProgressLabel, Tag,
  TagLabel, TagLeftIcon, Alert, AlertIcon, AlertDescription, Skeleton, Button,
  Code, useToast, Center, Spinner, Table, Thead, Tbody, Tr, Th, Td, TableContainer
} from '@chakra-ui/react';
import {
  MdBuild, MdRefresh, MdWarning, MdTrendingUp, MdTrendingDown, MdExpandMore,
  MdExpandLess, MdCheckCircle, MdTimeline, MdInsights, MdSavings, MdAnalytics,
  MdLightbulb, MdVerified, MdSpeed, MdArrowForward, MdBugReport, MdEngineering,
  MdDataUsage, MdAddAlert, MdCloudOff
} from 'react-icons/md';

import { motion } from 'framer-motion';
import analyticsService from '../../services/analyticsService';
import webSocketService from '../../services/websocketService'; 
// --- UPDATE (AI Explainer)
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer';

/** 0–1 or 0–100 → 0–100 (rounded); null when missing. */
const toPct = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.round((n <= 1 && n >= 0 ? n * 100 : n) * 10) / 10;
};
const money = (n) => (Number.isFinite(Number(n)) ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—');
// --- END UPDATE

// ============================================================================
// DEBUG CONFIGURATION
// ============================================================================
const DEBUG_CONFIG = {
  enabled: process.env.NODE_ENV === 'development',
  logToConsole: true,
};

// ============================================================================
// INTERNAL DEBUG LOGGER
// ============================================================================
class InternalDebugLogger {
  constructor(componentName) {
    this.componentName = componentName;
    this.enabled = DEBUG_CONFIG.enabled;
  }

  log(level, message, data = null) {
    if (!this.enabled || !DEBUG_CONFIG.logToConsole) return;
    
    const prefix = `[${this.componentName}][${level.toUpperCase()}]`;
    const styles = {
      debug: 'color: #888; font-size: 11px',
      info: 'color: #3182ce; font-weight: bold',
      warn: 'color: #d69e2e; font-weight: bold',
      error: 'color: #e53e3e; font-weight: bold',
      success: 'color: #38a169; font-weight: bold',
    };
    
    if (data) {
      console.log(`%c${prefix} ${message}`, styles[level] || styles.info, data);
    } else {
      console.log(`%c${prefix} ${message}`, styles[level] || styles.info);
    }
  }

  debug(message, data) { this.log('debug', message, data); }
  info(message, data) { this.log('info', message, data); }
  warn(message, data) { this.log('warn', message, data); }
  error(message, data) { this.log('error', message, data); }
  success(message, data) { this.log('success', message, data); }
}

// ============================================================================
// MOTION COMPONENTS
// ============================================================================
const MotionBox = motion(Box);

// ============================================================================
// UTILITY: SAFE IDENTIFIER PARSER
// ============================================================================
const parseAssetIdentifiers = (idString, fallbackName) => {
  if (!idString) return { name: fallbackName || 'Unknown Asset', id: 'N/A' };
  
  let name = fallbackName;
  let id = idString;

  if (idString.includes('-') && !idString.match(/^[0-9a-f]{8}-/i)) { 
    const parts = idString.split('-');
    id = parts.pop(); 
    const extractedName = parts.join('-');
    
    if (!name && extractedName && extractedName !== 'undefined') {
      name = extractedName;
    }
  }

  return {
    name: (!name || name === 'undefined') ? 'Target Asset' : name,
    id: id || 'N/A'
  };
};

// ============================================================================
// SUCCESS MODAL COMPONENT
// ============================================================================
const ActionSuccessModal = ({ isOpen, onClose, workOrderId, safeAssetId, safeAssetName, action }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const infoBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const successColor = action === 'Replace' ? 'orange.500' : 'green.500';

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay backdropFilter="blur(5px)" />
      <ModalContent borderRadius="2xl" bg={bgColor} p={2}>
        <ModalBody>
          <VStack spacing={6} py={4}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
            >
              <Circle size="80px" bg={`${successColor}10`} color={successColor}>
                <Icon as={MdCheckCircle} boxSize={12} />
              </Circle>
            </motion.div>

            <VStack spacing={1} textAlign="center">
              <Heading size="md">CapEx Action Initiated</Heading>
              <Text color="gray.500" fontSize="sm">
                The {action?.toLowerCase()} process for <b>{safeAssetName}</b> (ID: {safeAssetId}) has been generated as a priority work order.
              </Text>
            </VStack>

            <Box w="100%" p={4} bg={infoBg} borderRadius="xl" borderWidth="1px">
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between">
                  <Text fontSize="xs" fontWeight="bold" color="gray.500">WORK ORDER ID</Text>
                  <Badge colorScheme="blue" borderRadius="full" px={3}>{workOrderId || 'N/A'}</Badge>
                </HStack>
                <Divider />
                <HStack justify="space-between">
                  <Text fontSize="xs" fontWeight="bold" color="gray.500">ORIGINATOR</Text>
                  <Text fontSize="xs" fontWeight="bold">FlowSense ML Engine</Text>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter gap={3}>
          <Button variant="ghost" flex={1} onClick={onClose} borderRadius="full">
            Dismiss
          </Button>
          <Button 
            colorScheme="blue" 
            flex={1} 
            rightIcon={<MdArrowForward />} 
            borderRadius="full"
            onClick={() => window.location.href = `/admin/work-orders/${workOrderId}`}
            isDisabled={!workOrderId}
          >
            View Order
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

ActionSuccessModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  workOrderId: PropTypes.string,
  safeAssetId: PropTypes.string,
  safeAssetName: PropTypes.string,
  action: PropTypes.string
};

// ============================================================================
// CUSTOM RECHARTS TOOLTIP
// ============================================================================
const CustomRulTooltip = ({ active, payload, label, bg, border }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box bg={bg} p={3} borderRadius="lg" shadow="xl" border="1px solid" borderColor={border}>
        <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>{label}</Text>
        <HStack justify="space-between" mb={1}>
          <Text fontSize="sm" fontWeight="bold">Survival Probability:</Text>
          <Text fontSize="sm" fontWeight="bold" color={data.value < 50 ? 'red.500' : 'green.500'}>
            {data.value}%
          </Text>
        </HStack>
        <Text fontSize="10px" color="gray.500" maxW="200px" whiteSpace="normal">
          AI estimate of asset operating without catastrophic failure.
        </Text>
      </Box>
    );
  }
  return null;
};

// ============================================================================
// ANIMATED PROGRESS BAR
// ============================================================================
const AnimatedProgress = ({ value, colorScheme, label, amount, delay = 0 }) => {
  const trackBg = useColorModeValue('gray.100', 'gray.700');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const amountColor = useColorModeValue('gray.800', 'white');

  return (
    <MotionBox
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.3 }}
    >
      <HStack justify="space-between" mb={1}>
        <Text fontSize="sm" color={labelColor} fontWeight="500">
          {label}
        </Text>
        <Text fontSize="sm" fontWeight="bold" color={amountColor}>
          {amount}
        </Text>
      </HStack>
      <Box position="relative">
        <Progress
          value={value}
          size="sm"
          colorScheme={colorScheme}
          borderRadius="full"
          bg={trackBg}
          hasStripe
          isAnimated
        />
      </Box>
    </MotionBox>
  );
};

AnimatedProgress.propTypes = {
  value: PropTypes.number.isRequired,
  colorScheme: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  amount: PropTypes.string.isRequired,
  delay: PropTypes.number
};

// ============================================================================
// CONFIDENCE INDICATOR
// ============================================================================
const ConfidenceIndicator = ({ confidence = null }) => { // --- UPDATE: was a made-up default of 85
  const getConfidenceColor = (value) => {
    if (value >= 80) return 'green';
    if (value >= 60) return 'yellow';
    return 'red';
  };

  const known = confidence !== null && confidence !== undefined && Number.isFinite(Number(confidence)); // --- UPDATE
  const color = known ? getConfidenceColor(confidence) : 'gray';
  const ringBg = useColorModeValue('whiteAlpha.300', 'blackAlpha.300');

  return (
    <MotionBox
      initial={{ scale: 0, rotate: -180 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ duration: 0.5, type: 'spring' }}
    >
      <Tooltip label={known ? `ML Model Confidence: ${confidence}%` : 'The ML engine did not send a confidence for this prescription'} hasArrow placement="top">
        <Box position="relative">
          <CircularProgress
            value={known ? Number(confidence) : 0}
            color={`${color}.300`}
            trackColor={ringBg}
            size="60px"
            thickness="8px"
            capIsRound
          >
            <CircularProgressLabel>
              <VStack spacing={0}>
                <Text fontSize="xs" fontWeight="900" color="white">
                  {known ? `${confidence}%` : '—'}
                </Text>
              </VStack>
            </CircularProgressLabel>
          </CircularProgress>
          <Box
            position="absolute"
            bottom="-4px"
            left="50%"
            transform="translateX(-50%)"
          >
            <Tag size="sm" colorScheme={color} borderRadius="full" fontSize="9px" border="2px solid white">
              <TagLeftIcon as={MdVerified} boxSize={3} ml={1} />
              <TagLabel pr={1}>AI</TagLabel>
            </Tag>
          </Box>
        </Box>
      </Tooltip>
    </MotionBox>
  );
};

ConfidenceIndicator.propTypes = {
  confidence: PropTypes.number
};

// ============================================================================
// COST COMPARISON CARD
// ============================================================================
const CostComparisonCard = ({ label, amount, icon, color, isRecommended, subLabel }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue(`${color}.200`, `${color}.700`);
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const valueColor = useColorModeValue('gray.800', 'white');
  const iconBg = useColorModeValue(`${color}.50`, `${color}.900`);
  const iconColor = useColorModeValue(`${color}.500`, `${color}.300`);
  const recommendedBg = useColorModeValue(`${color}.50`, `${color}.900`);

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, boxShadow: 'lg' }}
      transition={{ duration: 0.2 }}
    >
      <Box
        p={4}
        bg={isRecommended ? recommendedBg : cardBg}
        borderRadius="xl"
        borderWidth="2px"
        borderColor={isRecommended ? `${color}.400` : borderColor}
        position="relative"
        overflow="hidden"
      >
        {isRecommended && (
          <Box position="absolute" top={2} right={2}>
            <Tag size="sm" colorScheme={color} borderRadius="full">
              <TagLeftIcon as={MdCheckCircle} boxSize={3} />
              <TagLabel fontSize="10px" fontWeight="bold">PRESCRIBED</TagLabel>
            </Tag>
          </Box>
        )}

        <HStack spacing={3} align="start">
          <Box p={2} bg={iconBg} borderRadius="lg">
            <Icon as={icon} boxSize={5} color={iconColor} />
          </Box>
          <VStack align="start" spacing={0} flex={1}>
            <Text fontSize="xs" color={labelColor} fontWeight="800" textTransform="uppercase" letterSpacing="0.05em">
              {label}
            </Text>
            <HStack align="baseline" spacing={1}>
              <Text fontSize="2xl" fontWeight="900" color={valueColor}>
                {typeof amount === 'number' ? `$${amount.toLocaleString()}` : '—'}{/* --- UPDATE (round 2) */}
              </Text>
              <Text fontSize="xs" color={labelColor} fontWeight="bold">/yr</Text>
            </HStack>
            {subLabel && (
              <Text fontSize="10px" color={labelColor} mt={1}>{subLabel}</Text>
            )}
          </VStack>
        </HStack>
      </Box>
    </MotionBox>
  );
};

CostComparisonCard.propTypes = {
  label: PropTypes.string.isRequired,
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired, // --- UPDATE (round 2): '—' when unknown
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string.isRequired,
  isRecommended: PropTypes.bool,
  subLabel: PropTypes.string
};

// ============================================================================
// INSIGHT CHIP
// ============================================================================
const InsightChip = ({ icon, label, value, color = 'blue' }) => {
  const chipBg = useColorModeValue(`${color}.50`, `${color}.900`);
  const chipColor = useColorModeValue(`${color}.700`, `${color}.200`);
  const borderColor = useColorModeValue(`${color}.200`, `${color}.700`);

  return (
    <MotionBox
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.05 }}
    >
      <HStack bg={chipBg} px={3} py={1.5} borderRadius="full" spacing={2} border="1px solid" borderColor={borderColor}>
        <Icon as={icon} color={chipColor} boxSize={4} />
        <Text fontSize="xs" color={chipColor} fontWeight="600">
          {label}: <Text as="span" fontWeight="800">{value}</Text>
        </Text>
      </HStack>
    </MotionBox>
  );
};

InsightChip.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  color: PropTypes.string
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const PrescriptiveActionCard = ({ prescription, isLoading = false, debugLogger = null, onApplied }) => {
  // Initialize internal logger
  const internalLogger = useRef(new InternalDebugLogger('PrescriptiveActionCard')).current;
  const logger = debugLogger || internalLogger;
  const toast = useToast();

  // State
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  
  // LIVE WEBSOCKET STATE (Prevents card from going stale)
  const [liveAction, setLiveAction] = useState(prescription?.action || 'Unknown');
  const [liveConfidence, setLiveConfidence] = useState(toPct(prescription?.confidence)); // --- UPDATE: null when unknown, 0–1 → %
  const [lastLiveAt, setLastLiveAt] = useState(null); // --- UPDATE
  const receivedAtRef = useRef(new Date()); // --- UPDATE
  
  // Modals & Collapses
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isSuccessOpen, onOpen: onSuccessOpen, onClose: onSuccessClose } = useDisclosure();
  const { isOpen: isExpanded, onToggle: toggleExpanded } = useDisclosure({ defaultIsOpen: true });
  
  // Telemetry details state
  const [detailsData, setDetailsData] = useState({ history: [], rulTrend: [] });
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [createdWoId, setCreatedWoId] = useState('');

  // --------------------------------------------------------------------------
  // THEME COLORS & HOOKS 
  // --------------------------------------------------------------------------
  const cardBg = useColorModeValue('white', 'gray.800');
  const dividerColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const sectionBg = useColorModeValue('gray.50', 'gray.900');
  const debugBg = useColorModeValue('yellow.50', 'yellow.900');
  const debugBorder = useColorModeValue('yellow.200', 'yellow.700');
  const modalChartBg = useColorModeValue('gray.50', 'gray.800');
  const modalHistoryBg = useColorModeValue('gray.50', 'gray.700');
  const statBoxBg = useColorModeValue('gray.50', 'gray.700');
  const chartRulBg = useColorModeValue('gray.50', 'gray.900');
  
  // Dynamic Hooks based on action type
  const isReplace = liveAction.toLowerCase() === "replace";
  const statusColor = isReplace ? "red" : "green";
  const actionIcon = isReplace ? MdRefresh : MdBuild;

  const borderColor = useColorModeValue(
    isReplace ? 'red.200' : 'green.200',
    isReplace ? 'red.700' : 'green.700'
  );
  const reasonBg = useColorModeValue(
    isReplace ? 'red.50' : 'green.50',
    isReplace ? 'red.900' : 'green.900'
  );
  const reasonColor = useColorModeValue(
    isReplace ? 'red.800' : 'green.800',
    isReplace ? 'red.200' : 'green.200'
  );
  const headerGradient = useColorModeValue(
    isReplace ? 'linear(to-r, red.500, orange.400)' : 'linear(to-r, teal.400, green.400)',
    isReplace ? 'linear(to-r, red.700, orange.600)' : 'linear(to-r, teal.600, green.600)'
  );
  const reasonBannerBg = useColorModeValue(`${statusColor}.50`, `${statusColor}.900`);
  const reasonBannerText = useColorModeValue(`${statusColor}.800`, `${statusColor}.100`);

  // --------------------------------------------------------------------------
  // COMPUTED VALUES 
  // --------------------------------------------------------------------------
  
  // Data parsing explicitly grabs assetName
  const { name: safeAssetName, id: safeAssetId } = useMemo(() => {
    return parseAssetIdentifiers(prescription?.assetId, prescription?.assetName);
  }, [prescription]);

  // Financial Math Variables (Safe extraction)
  const costNew = Number(prescription?.analysis?.annualized_cost_of_new_asset) || 0;
  const costRepair = Number(prescription?.analysis?.annualized_cost_of_repair) || 0;
  // --- UPDATE (round 2): which costs the engine actually sent (null/'' → not known → "—")
  const knownCost = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
  const newKnown = knownCost(prescription?.analysis?.annualized_cost_of_new_asset);
  const repairKnown = knownCost(prescription?.analysis?.annualized_cost_of_repair);
  // --- END UPDATE
  
  const savingsAmount = useMemo(() => {
    return Math.abs(costNew - costRepair);
  }, [costNew, costRepair]);

  const savingsPercentage = useMemo(() => {
    const higher = Math.max(costNew, costRepair);
    if (higher === 0) return 0;
    return ((savingsAmount / higher) * 100).toFixed(1);
  }, [costNew, costRepair, savingsAmount]);

  // --- UPDATE (AI Explainer): real ML metrics + rule findings for the explainer
  const analysis = useMemo(() => prescription?.analysis || {}, [prescription]);
  const failurePct = toPct(analysis.failure_probability);
  const efficiencyPct = toPct(analysis.efficiency_rating);
  const hasCosts = costNew > 0 || costRepair > 0;
  const hasMlMetrics = analysis.remaining_lifespan != null || failurePct !== null
    || analysis.maintenance_frequency != null || efficiencyPct !== null;
  const generatedAt = prescription?.generated_at || prescription?.created_at || prescription?.timestamp || null;

  const explainerFindings = useMemo(() => {
    const out = [];
    const isRep = String(liveAction).toLowerCase() === 'replace';
    if (!hasCosts) {
      out.push({ severity: 'medium', text: 'The ML engine sent no annualised costs, so the repair-or-replace saving cannot be checked.' });
    } else {
      const cheaper = costNew < costRepair ? 'replace' : costRepair < costNew ? 'repair' : null;
      if (cheaper && cheaper !== (isRep ? 'replace' : 'repair')) {
        out.push({ severity: 'high', text: `The prescribed action (${isRep ? 'replace' : 'repair'}) is not the cheaper option on annualised cost — check the other drivers before executing.`, evidence: `Replace ${money(costNew)}/yr · repair ${money(costRepair)}/yr` });
      } else if (cheaper) {
        out.push({ severity: 'ok', text: `${isRep ? 'Replacing' : 'Repairing'} costs less per year — about ${money(savingsAmount)} (${savingsPercentage} %).`, evidence: `Replace ${money(costNew)}/yr · repair ${money(costRepair)}/yr` });
      } else {
        out.push({ severity: 'info', text: 'Both options cost the same per year; the decision rests on risk and remaining life.' });
      }
    }
    if (liveConfidence === null) out.push({ severity: 'medium', text: 'No model confidence was sent with this prescription.' });
    else if (liveConfidence < 60) out.push({ severity: 'high', text: `Model confidence is low (${liveConfidence} %) — get a field inspection before committing capital.` });
    else if (liveConfidence < 80) out.push({ severity: 'medium', text: `Model confidence is moderate (${liveConfidence} %).` });
    if (failurePct !== null && failurePct >= 50) out.push({ severity: 'high', text: `Failure risk is ${failurePct} %.` });
    if (analysis.remaining_lifespan != null) out.push({ severity: 'info', text: `Remaining useful life estimate: ${analysis.remaining_lifespan}.` });
    if (!prescription?.reason) out.push({ severity: 'info', text: 'The ML engine did not send a reason text for this prescription.' });
    return out;
  }, [liveAction, hasCosts, costNew, costRepair, savingsAmount, savingsPercentage, liveConfidence, failurePct, analysis.remaining_lifespan, prescription]);

  const explainerFacts = useMemo(() => ({
    asset: { id: safeAssetId, name: safeAssetName },
    prescribed_action: liveAction,
    reason: prescription?.reason || null,
    model_confidence_pct: liveConfidence,
    annualised_cost_replace: hasCosts ? costNew : null,
    annualised_cost_repair: hasCosts ? costRepair : null,
    saving_per_year: hasCosts ? savingsAmount : null,
    saving_pct: hasCosts ? Number(savingsPercentage) : null,
    remaining_life: analysis.remaining_lifespan ?? null,
    failure_probability_pct: failurePct,
    maintenance_frequency: analysis.maintenance_frequency ?? null,
    efficiency_pct: efficiencyPct,
    method: 'Life-cycle cost analysis (LCCA) from the FlowSense ML engine',
  }), [safeAssetId, safeAssetName, liveAction, prescription, liveConfidence, hasCosts, costNew, costRepair, savingsAmount, savingsPercentage, analysis, failurePct, efficiencyPct]);
  // --- END UPDATE


  // --------------------------------------------------------------------------
  // EFFECTS & WEBSOCKETS
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (prescription) {
      logger.info('PrescriptiveActionCard rendered', {
        action: liveAction,
        parsedId: safeAssetId,
        parsedName: safeAssetName,
        confidence: liveConfidence,
        savingsAmount,
      });
    }
  }, [prescription, safeAssetId, safeAssetName, liveConfidence, liveAction, savingsAmount, logger]);

  // WebSocket Listener keeps the card alive and accurate while waiting in the queue
  useEffect(() => {
    const handleWsUpdate = (raw) => {
      // If the backend ML model recalculates RUL, it pushes an update. 
      // If this card is for the updated asset, we dynamically adjust the UI.
      // --- UPDATE: subscribed by type, so the payload may have no `type`; FlowSense sends asset_id
      const data = raw?.payload || raw?.data || raw || {};
      if (raw?.type && raw.type !== 'asset_prediction_update') return;
      const id = String(data.assetId ?? data.asset_id ?? '');
      if (!id || id !== String(safeAssetId)) return;
      logger.info(`Live WebSocket update received for ${safeAssetId}`, data);
      const c = toPct(data.confidence ?? data.confidence_score);
      if (c !== null) setLiveConfidence(c);
      if (data.action) setLiveAction(String(data.action));
      setLastLiveAt(new Date());
      // --- END UPDATE
    };

    webSocketService.subscribe('asset_prediction_update', handleWsUpdate);
    
    return () => {
      webSocketService.unsubscribe('asset_prediction_update', handleWsUpdate);
    };
  }, [safeAssetId, logger]);

  // --------------------------------------------------------------------------
  // EARLY RETURNS
  // --------------------------------------------------------------------------
  if (isLoading) {
    return (
      <Box p={5} shadow="xl" borderRadius="2xl" bg={cardBg} borderWidth="1px" borderColor={dividerColor}>
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between">
            <Skeleton height="32px" width="200px" borderRadius="md" />
            <Skeleton height="60px" width="60px" borderRadius="full" />
          </HStack>
          <Skeleton height="60px" borderRadius="lg" />
          <Skeleton height="100px" borderRadius="lg" />
        </VStack>
      </Box>
    );
  }

  if (!prescription) return null;

  // --------------------------------------------------------------------------
  // HANDLERS
  // --------------------------------------------------------------------------
  const handleDetailsClick = async () => {
    logger.info('Telemetry Details requested', { assetId: safeAssetId });

    if (safeAssetId === 'N/A') {
      toast({ title: "Error", description: "Valid Asset ID is missing.", status: "error", duration: 3000 });
      return;
    }

    onModalOpen();
    setIsDetailsLoading(true);

    try {
      const response = await analyticsService.getAssetLifecycleDetails(safeAssetId);
      if (response.success) {
        setDetailsData(response);
      } else {
        throw new Error(response.message || 'Failed to fetch telemetry details');
      }
    } catch (err) {
      logger.error('Failed to fetch lifecycle details', err);
      toast({ 
        title: "Telemetry Sync Failed", 
        description: "Could not fetch real telemetry data from FlowSense ML Engine.", 
        status: "error", 
        duration: 3000 
      });
      // Set to an empty state rather than returning fake data
      setDetailsData({ history: [], rulTrend: [] });
    } finally {
      setIsDetailsLoading(false);
    }
  };

  const handleApplyClick = async () => {
    logger.info('Apply button clicked', { assetId: safeAssetId, action: liveAction });
    setIsApplying(true);

    try {
      // Constructing perfect payload for backend reinforcement learning
      const response = await analyticsService.applyPrescriptiveAction({
        assetId: safeAssetId,
        assetName: safeAssetName, 
        action: liveAction,
        reason: prescription.reason,
        analysis: prescription.analysis, 
        confidence: liveConfidence,
        savings_estimate: savingsAmount 
      });

      if (response.success) {
        const newWoId = response.workOrderId;
        if (!newWoId) throw new Error("Backend failed to generate a valid Work Order ID");

        setCreatedWoId(newWoId);
        onSuccessOpen();
        
        // Pass the full actionData back to parent so the card is cleanly removed from queue
        if (typeof onApplied === 'function') {
          setTimeout(() => {
            onApplied(safeAssetId, {
              workOrderId: newWoId,
              action: liveAction,
              savings: savingsAmount
            });
          }, 1500); // Allow user to see success modal briefly before unmounting
        }
      } else {
        throw new Error(response.message || 'Failed to apply recommendation');
      }
    } catch (err) {
      logger.error('Failed to apply recommendation', err);
      toast({
        title: "Application Failed",
        description: err.message || "Could not route prescription to Work Order system.",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsApplying(false);
    }
  };

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <>
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <Box shadow="xl" borderRadius="2xl" bg={cardBg} borderWidth="2px" borderColor={borderColor} overflow="hidden" _hover={{ shadow: '2xl', borderColor: `${statusColor}.400` }} transition="all 0.3s">
          
          {/* ====================== HEADER ====================== */}
          <Box bgGradient={headerGradient} px={6} py={5}>
            <Flex justify="space-between" align="center">
              <HStack spacing={4}>
                <MotionBox initial={{ rotate: -180, scale: 0 }} animate={{ rotate: 0, scale: 1 }} transition={{ duration: 0.5, type: 'spring' }}>
                  <Box bg="whiteAlpha.300" p={3} borderRadius="xl" backdropFilter="blur(8px)">
                    <Icon as={actionIcon} color="white" boxSize={6} />
                  </Box>
                </MotionBox>
                <VStack align="start" spacing={0}>
                  <HStack>
                    <Icon as={MdLightbulb} color="yellow.300" boxSize={4} />
                    <Text fontSize="xs" color="whiteAlpha.900" fontWeight="800" textTransform="uppercase" letterSpacing="0.05em">
                      ML LCCA Output
                    </Text>
                  </HStack>
                  <Text fontSize="2xl" fontWeight="900" color="white" lineHeight="1.2">
                    {liveAction.toUpperCase()} {safeAssetName.toUpperCase()}
                  </Text>
                  <Text fontSize="xs" color="whiteAlpha.800" fontFamily="mono" mt={1}>
                    ID: {safeAssetId}
                  </Text>
                </VStack>
              </HStack>
              <ConfidenceIndicator confidence={liveConfidence} />
            </Flex>
          </Box>

          {/* ====================== BODY ====================== */}
          <VStack align="stretch" spacing={5} p={6}>
            
            {/* Reason Box */}
            <MotionBox initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <Box bg={reasonBannerBg} p={4} borderRadius="xl" borderWidth="1px" borderColor={borderColor} position="relative" overflow="hidden">
                <Box position="absolute" left={0} top={0} bottom={0} w="4px" bgGradient={headerGradient} />
                <HStack spacing={3} pl={2}>
                  <Icon as={MdInsights} color={reasonColor} boxSize={5} mt={0.5} />
                  <Text fontSize="sm" color={reasonBannerText} fontWeight="600" lineHeight="tall">
                    {prescription.reason || "Analysis indicates action is required based on lifecycle parameters."}
                  </Text>
                </HStack>
              </Box>
            </MotionBox>

            {/* Quick Insights */}
            <Flex wrap="wrap" gap={3}>
              <InsightChip icon={MdSavings} label="Projected Savings" value={`$${savingsAmount.toLocaleString()}/yr`} color={statusColor} />
              <InsightChip icon={MdSpeed} label="Financial Efficiency" value={`+${savingsPercentage}%`} color="blue" />
              <InsightChip icon={MdTimeline} label="Analysis Model" value="Lifecycle Costing (LCCA)" color="purple" />
            </Flex>

            {/* --- UPDATE (AI Explainer) */}
            <FlowSenseAIExplainer
              compact
              module="asset_lifecycle"
              title={`Why ${String(liveAction).toLowerCase()}?`}
              subtitle="Checks this prescription's own LCCA numbers, then explains them in plain language"
              facts={explainerFacts}
              findings={explainerFindings}
              defaultQuestion={`Explain in plain language why FlowSense recommends to ${String(liveAction).toLowerCase()} ${safeAssetName}, what the risk is if we wait, and what to check first.`}
              questions={['What happens if we wait 6 months?', 'What should the crew check first?', 'How reliable is this recommendation?']}
            />
            {/* --- END UPDATE */}

            {/* Analysis Section Toggle */}
            {prescription.analysis && (
              <>
                <Flex justify="space-between" align="center" borderBottomWidth="1px" borderColor={dividerColor} pb={2} mt={2}>
                  <HStack>
                    <Icon as={MdAnalytics} color={mutedColor} boxSize={5} />
                    <Text fontSize="xs" fontWeight="800" color={mutedColor} textTransform="uppercase" letterSpacing="0.05em">
                      Financial Breakdown
                    </Text>
                  </HStack>
                  <HStack>
                    {DEBUG_CONFIG.enabled && (
                      <Tooltip label="Toggle Debug Info" hasArrow>
                        <IconButton icon={<MdBugReport />} size="xs" variant="ghost" colorScheme="yellow" onClick={() => setShowDebugInfo(!showDebugInfo)} borderRadius="full" />
                      </Tooltip>
                    )}
                    <Tooltip label={isExpanded ? "Collapse" : "Expand"} hasArrow>
                      <IconButton icon={<Icon as={isExpanded ? MdExpandLess : MdExpandMore} />} size="xs" variant="ghost" onClick={toggleExpanded} borderRadius="full" />
                    </Tooltip>
                  </HStack>
                </Flex>

                <Collapse in={isExpanded} animateOpacity>
                  <VStack align="stretch" spacing={5}>
                    
                    {/* Cost Cards */}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <CostComparisonCard
                        label="Cost to Replace (Annualized)"
                        amount={newKnown ? costNew : '—'} /* --- UPDATE (round 2) */
                        icon={MdRefresh} color="red" isRecommended={isReplace} subLabel="Includes new hardware + installation labor"
                      />
                      <CostComparisonCard
                        label="Cost to Repair (Annualized)"
                        amount={repairKnown ? costRepair : '—'} /* --- UPDATE (round 2) */
                        icon={MdEngineering} color="blue" isRecommended={!isReplace} subLabel="Based on historical MC/RAV failure rates"
                      />
                    </SimpleGrid>

                    {/* Progress Bars */}
                    <Box bg={sectionBg} p={4} borderRadius="xl" borderWidth="1px" borderColor={dividerColor}>
                      <VStack align="stretch" spacing={4}>
                        <AnimatedProgress
                          value={Math.min((costNew / Math.max(costNew, costRepair, 1)) * 100, 100)} colorScheme="blue" label="New Asset Investment Baseline" /* --- UPDATE: real share, was a fixed 40 */
                          amount={newKnown ? `$${costNew.toLocaleString()}/yr` : '—'} delay={0.1}
                        />
                        <AnimatedProgress
                          value={Math.min((costRepair / Math.max(costNew, costRepair, 1)) * 100, 100)} /* --- UPDATE: same scale as the bar above */
                          colorScheme={statusColor} label="Repair & Maintenance Projection"
                          amount={repairKnown ? `$${costRepair.toLocaleString()}/yr` : '—'} delay={0.2}
                        />
                      </VStack>
                    </Box>

                    {/* Savings Alert Banner */}
                    <MotionBox initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                      <Alert status={isReplace ? "warning" : "success"} variant="subtle" borderRadius="xl">
                        <AlertIcon />
                        <AlertDescription fontSize="sm">
                          <Text as="span" fontWeight="bold">
                            {isReplace ? "Replacement recommended:" : "Repair recommended:"}
                          </Text>{" "}
                          By choosing to {isReplace ? "replace" : "repair"}, you could save approximately{" "}
                          <Text as="span" fontWeight="bold" color={`${statusColor}.600`}>
                            ${savingsAmount.toLocaleString()}/year
                          </Text>{" "}
                          ({savingsPercentage}% cost reduction over the lifecycle).
                        </AlertDescription>
                      </Alert>
                    </MotionBox>

                    {/* Additional Machine Learning Metrics */}
                    {(prescription.analysis.additional_metrics || hasMlMetrics) && ( /* --- UPDATE: shown whenever the ML engine sent them */
                      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                        {prescription.analysis.remaining_lifespan && (
                          <Box bg={statBoxBg} p={3} borderRadius="lg" textAlign="center" borderWidth="1px" borderColor={dividerColor}>
                            <Text fontSize="10px" color={mutedColor} fontWeight="800" textTransform="uppercase">RUL Estimate</Text>
                            <Text fontSize="lg" fontWeight="900" color={textColor}>{prescription.analysis.remaining_lifespan}</Text>
                          </Box>
                        )}
                        {failurePct !== null && ( /* --- UPDATE: 0 is a value; 0–1 shown as % */
                          <Box bg={statBoxBg} p={3} borderRadius="lg" textAlign="center" borderWidth="1px" borderColor={dividerColor}>
                            <Text fontSize="10px" color={mutedColor} fontWeight="800" textTransform="uppercase">Failure Risk</Text>
                            <Text fontSize="lg" fontWeight="900" color={failurePct >= 50 ? 'red.500' : failurePct >= 25 ? 'orange.400' : 'green.500'}>{failurePct}%</Text>
                          </Box>
                        )}
                        {prescription.analysis.maintenance_frequency && (
                          <Box bg={statBoxBg} p={3} borderRadius="lg" textAlign="center" borderWidth="1px" borderColor={dividerColor}>
                            <Text fontSize="10px" color={mutedColor} fontWeight="800" textTransform="uppercase">Maint. Freq</Text>
                            <Text fontSize="lg" fontWeight="900" color={textColor}>{prescription.analysis.maintenance_frequency}</Text>
                          </Box>
                        )}
                        {efficiencyPct !== null && (
                          <Box bg={statBoxBg} p={3} borderRadius="lg" textAlign="center" borderWidth="1px" borderColor={dividerColor}>
                            <Text fontSize="10px" color={mutedColor} fontWeight="800" textTransform="uppercase">OEE Score</Text>
                            <Text fontSize="lg" fontWeight="900" color="green.500">{efficiencyPct}%</Text>
                          </Box>
                        )}
                      </SimpleGrid>
                    )}
                  </VStack>
                </Collapse>
              </>
            )}

            {/* Debug Console (Developer Mode Only) */}
            <Collapse in={showDebugInfo}>
              <Box p={3} bg="blackAlpha.900" color="green.300" borderRadius="md" fontFamily="mono" fontSize="10px" mt={2}>
                <Text fontWeight="bold" color="white" mb={2}>// SYSTEM.DEBUG.LCCA</Text>
                <Text>RAW_ID: {prescription.assetId}</Text>
                <Text>PARSED_ID: {safeAssetId}</Text>
                <Text>PARSED_NAME: {safeAssetName}</Text>
                <Text>ALG_CONF: {liveConfidence}%</Text>
                <Text>NEW_COST: {costNew}</Text>
                <Text>REP_COST: {costRepair}</Text>
                <Text>ACTION: {liveAction}</Text>
              </Box>
            </Collapse>
          </VStack>

          {/* ====================== FOOTER ====================== */}
          <Box bg={sectionBg} px={6} py={4} borderTopWidth="1px" borderColor={dividerColor}>
            <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
              <Text fontSize="xs" color={mutedColor} fontWeight="600">
                <Icon as={MdDataUsage} mr={1} />
                {/* --- UPDATE: the prescription's own time; was the render time */}
                {generatedAt ? `LCCA generated ${new Date(generatedAt).toLocaleString()}` : `Received ${receivedAtRef.current.toLocaleTimeString()}`}
                {lastLiveAt && <Badge ml={2} colorScheme="green" variant="subtle" borderRadius="full" fontSize="2xs">live update {lastLiveAt.toLocaleTimeString()}</Badge>}
              </Text>
              <HStack spacing={3}>
                <Button size="sm" variant="outline" colorScheme="gray" leftIcon={<MdTimeline />} borderRadius="full" onClick={handleDetailsClick}>
                  View Telemetry
                </Button>
                <Button size="sm" colorScheme={statusColor} leftIcon={<MdCheckCircle />} borderRadius="full" onClick={handleApplyClick} isLoading={isApplying} shadow="sm">
                  Execute CapEx Route
                </Button>
              </HStack>
            </Flex>
          </Box>

        </Box>
      </MotionBox>

      {/* ====================== MODALS ====================== */}
      <ActionSuccessModal 
        isOpen={isSuccessOpen} 
        onClose={onSuccessClose} 
        workOrderId={createdWoId} 
        safeAssetId={safeAssetId}
        safeAssetName={safeAssetName}
        action={liveAction} 
      />

      <Modal isOpen={isModalOpen} onClose={onModalClose} size="4xl">
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="2xl" bg={cardBg}>
          <ModalHeader borderBottomWidth="1px" pb={4} borderColor={dividerColor}>
            <Heading size="md" color={textColor}>Asset Telemetry & Survival Analysis</Heading>
            <HStack mt={2}>
              <Badge colorScheme="blue">ID: {safeAssetId}</Badge>
              <Text fontSize="sm" color={mutedColor} fontWeight="bold">{safeAssetName}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody py={6}>
            {isDetailsLoading ? (
              <Center py={20}>
                <VStack>
                  <Spinner size="xl" color="blue.500" thickness="4px" />
                  <Text mt={4} color={mutedColor} fontWeight="600">Querying ML Inference Engine...</Text>
                </VStack>
              </Center>
            ) : (
              <VStack spacing={8} align="stretch">
                
                {/* Information Banner */}
                <Alert status="info" borderRadius="xl" variant="left-accent">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    This chart maps the <b>Random Survival Forest</b> output. It visualizes the probability that this asset will continue to function without critical failure over the next 12 months.
                  </AlertDescription>
                </Alert>

                {/* RUL Trend Chart */}
                <Box h="300px" bg={chartRulBg} p={5} borderRadius="xl" borderWidth="1px" borderColor={dividerColor}>
                  <Text fontSize="xs" fontWeight="800" mb={4} color={mutedColor} textTransform="uppercase" letterSpacing="0.05em">
                    Probability of Survival Curve
                  </Text>
                  {detailsData.rulTrend?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="85%">
                      <AreaChart data={detailsData.rulTrend}>
                        <defs>
                          <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3182ce" stopOpacity={0.4}/>
                            <stop offset="95%" stopColor="#3182ce" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={dividerColor} />
                        <XAxis dataKey="date" fontSize="10px" stroke={mutedColor} tickMargin={10} />
                        <YAxis fontSize="10px" stroke={mutedColor} unit="%" domain={[0, 100]} />
                        <RechartsTooltip content={<CustomRulTooltip bg={cardBg} border={dividerColor} />} />
                        <Area type="monotone" dataKey="value" stroke="#3182ce" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <Center h="100%">
                      <VStack spacing={2} opacity={0.6}>
                        <Icon as={MdCloudOff} boxSize={8} color={mutedColor} />
                        <Text fontSize="sm" color={mutedColor} fontWeight="500">Survival analysis data pending ML generation.</Text>
                      </VStack>
                    </Center>
                  )}
                </Box>

                {/* Maintenance History */}
                <Box>
                  <Text fontSize="xs" fontWeight="800" mb={4} color={mutedColor} textTransform="uppercase" letterSpacing="0.05em">
                    Historical Financial Burden (MC/RAV Drivers)
                  </Text>
                  <VStack align="stretch" spacing={3}>
                    {detailsData.history?.length > 0 ? (
                      detailsData.history.map((h, i) => (
                        <HStack key={i} justify="space-between" p={4} bg={modalHistoryBg} borderRadius="xl" borderWidth="1px" borderColor={dividerColor} _hover={{ shadow: 'sm' }} transition="all 0.2s">
                          <HStack spacing={4}>
                            <Circle size="40px" bg="blue.100"><Icon as={MdBuild} color="blue.500" /></Circle>
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="bold" color={textColor}>{h.type || 'Maintenance Event'}</Text>
                              <Text fontSize="xs" color={mutedColor}>{h.date}</Text>
                            </VStack>
                          </HStack>
                          <Badge colorScheme="red" fontSize="md" px={3} py={1} borderRadius="lg">
                            {Number.isFinite(Number(h.cost)) ? `$${Number(h.cost).toLocaleString()}` : '—' /* --- UPDATE: no cost ≠ $0 */}
                          </Badge>
                        </HStack>
                      ))
                    ) : (
                      <Center p={8} bg={modalHistoryBg} borderRadius="xl" borderWidth="1px" borderColor={dividerColor}>
                        <Text fontSize="sm" color={mutedColor}>No historical financial records found for this asset.</Text>
                      </Center>
                    )}
                  </VStack>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter borderTopWidth="1px" borderColor={dividerColor}>
            {/* --- UPDATE: what came back + retry */}
            {!isDetailsLoading && (
              <Text fontSize="xs" color={mutedColor} mr="auto">
                {detailsData.rulTrend?.length || 0} survival points · {detailsData.history?.length || 0} cost records
              </Text>
            )}
            <Button variant="outline" onClick={handleDetailsClick} borderRadius="full" mr={2} size="sm" leftIcon={<MdRefresh />} isLoading={isDetailsLoading}>Retry</Button>
            {/* --- END UPDATE */}
            <Button variant="ghost" onClick={onModalClose} borderRadius="full">Close Dashboard</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

PrescriptiveActionCard.propTypes = {
  prescription: PropTypes.shape({
    assetId: PropTypes.string,
    assetName: PropTypes.string,
    action: PropTypes.string.isRequired,
    reason: PropTypes.string,
    confidence: PropTypes.number,
    analysis: PropTypes.shape({
      annualized_cost_of_new_asset: PropTypes.number,
      annualized_cost_of_repair: PropTypes.number,
      remaining_lifespan: PropTypes.string,
      failure_probability: PropTypes.number,
      maintenance_frequency: PropTypes.string,
      efficiency_rating: PropTypes.number,
      additional_metrics: PropTypes.bool
    })
  }),
  isLoading: PropTypes.bool,
  debugLogger: PropTypes.object,
  onApplied: PropTypes.func
};

export default PrescriptiveActionCard;